import { debugStore } from '@stores/Debug';
import { SegmentType } from '@stores/Library/LibrarySearch';

export type ExamplePromptPart = {
  text: string;
  type: 'correct' | 'incorrect';
};

export type SearchTypeOptionData = {
  type: SegmentType;
  title: string;
  subtitle?: string;
  visual: boolean;
  conversational: boolean;
  experimental?: boolean;
  examplePrompt: ExamplePromptPart[];
};

export const SEARCH_TYPE_OPTIONS: SearchTypeOptionData[] = [
  {
    type: 'visual',
    title: 'Visual search',
    visual: true,
    conversational: false,
    examplePrompt: [
      {
        text: 'A musician with a guitar under a single spotlight in a smoky room, ',
        type: 'correct',
      },
      { text: 'singing "This stage is my only home".', type: 'incorrect' },
    ],
  },
  {
    type: 'transcript',
    title: 'Conversational search',
    visual: false,
    conversational: true,
    examplePrompt: [
      {
        text: 'A musician with a guitar under a single spotlight in a smoky room, singing ',
        type: 'incorrect',
      },
      { text: '"This stage is my only home".', type: 'correct' },
    ],
  },
  {
    type: 'unified',
    title: 'Unified search',
    subtitle: '(Visual & Conversational search)',
    visual: true,
    conversational: true,
    experimental: true,
    examplePrompt: [
      {
        text: 'A musician with a guitar under a single spotlight in a smoky room, singing about the stage as his only home.',
        type: 'correct',
      },
    ],
  },
];

export function getSearchTypeOption(type: SegmentType): SearchTypeOptionData | undefined {
  return SEARCH_TYPE_OPTIONS.find((o) => o.type === type);
}

/**
 * The search modes currently selectable in the UI — unified only while the
 * debug flag revives it. Single source for "which modes exist right now":
 * consumers rendering mode pickers should map THIS, not SEARCH_TYPE_OPTIONS,
 * so they can't accidentally surface the disabled mode.
 */
export function availableSearchTypeOptions(): SearchTypeOptionData[] {
  return SEARCH_TYPE_OPTIONS.filter((o) => o.type !== 'unified' || debugStore.showUnifiedSearch);
}

/** Whether a raw value (e.g. a URL param) names a currently-selectable mode. */
export function isSelectableSearchType(value: string | null): value is SegmentType {
  return availableSearchTypeOptions().some((o) => o.type === value);
}

/**
 * The image-capable search modes, as copy: "visual search" normally, "visual or
 * unified search" while the unified-search debug flag revives the mode. Keeps
 * user-facing strings from naming a mode that isn't selectable.
 */
export function visualSearchModesPhrase(): string {
  return debugStore.showUnifiedSearch ? 'visual or unified search' : 'visual search';
}
